import React, { useState, useEffect } from 'react'
import { Card, Group, Text, Box, Grid, Avatar, Badge, Flex, SimpleGrid, Anchor, Title, NumberFormatter, ActionIcon, Button, Skeleton } from '@mantine/core'
import dayjs from 'dayjs'
import { IconPrinter, IconTrash, IconArrowNarrowRight } from '@tabler/icons-react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { getDetailUser } from '../../../services/users'
import { getPackageShipmentList } from '../../../services/package'
import { deleteOrder } from '../../../services/orders'
import { modalDeleteData } from '../../ui/Prompt/modalDeleteData'
import { notificationError } from '../../ui/Notifications'

const defaultUser = {
  id: null,
  name: ''
}


const OrderList = ({ data, type, reloadList }) => {
  const isLoadingUser = useSelector(state => state.user_login.isLoadingUser)
  const navigate = useNavigate()
  const [loadingUser, setLoadingUser] = useState(true)
  const [detailUser, setDetailUser] = useState(defaultUser)
  const [loadingAgent, setLoadingAgent] = useState(true)
  const [detailAgent, setDetailAgent] = useState(defaultUser)
  const [loadingPackageShipment, setLoadingPackageShipment] = useState(true)
  const [packageShipment, setPackageShipment] = useState([])


  const handleGetUserOrderBy = async(userId) => {
    setLoadingUser(true)
    try {
      const response = await handleGetDetailUser(userId)
      const dataUser = response.data
      const remapData = {
        id: dataUser.attributes.id,
        name: dataUser.attributes.name
      }
      setDetailUser(remapData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingUser(false)
    }
  }

  const handleGetUserAgent = async(userId) => {
    setLoadingAgent(true)
    try {
      const response = await handleGetDetailUser(userId)
      const dataAgent = response.data
      const remapData = {
        id: dataAgent.attributes.id,
        name: dataAgent.attributes.name
      }
      setDetailAgent(remapData)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingAgent(false)
    }
  }

  const handleGetDetailUser = async(userId) => {
    try {
      const params = {
        'fields[user]': 'name',
      }
      const response = await getDetailUser(userId, params)
      return response
    } catch (error) {
      console.log(error)
    }
  }

  const handleGetPackageShipment = async(orderId) => {
    setLoadingPackageShipment(true)
    const params = {
      'filter[order_id][in]': orderId,
      timestamp: `${new Date().getTime()}`,
      'fields[package]': 'id',
    }
    try {
      const response = await getPackageShipmentList(params)
      setPackageShipment(response.data)
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingPackageShipment(false)
    }
  }

  useEffect(() => {
    if (data) {
      handleGetUserOrderBy(data.orderById)
      handleGetPackageShipment(data.id)
      if (data.agentId !== null) {
        handleGetUserAgent(data.agentId)
      } else {
        setLoadingAgent(false)
      }
    }
    // eslint-disable-next-line
  }, [data])

  const loadingDataUser = () => {
    return (
      <Flex gap='sm' align='center' mb={14}>
        <Skeleton height={40} circle />
        <Box w={100}>
          <Skeleton height={14} radius="xl" />
        </Box>
      </Flex>
    )
  }

  const mappingDataUser = (data) => {
    return (
      <Flex gap='sm' align='center' mb={16}>
        <Avatar variant='filled' name={data.name} radius="xl" size="md" color='red' />
        <Box>
          <Text fz={13} fw='bold' lineClamp={2}>{data.name}</Text>
        </Box>
      </Flex>
    )
  }

  const handlePrintOrder = (invoiceUrl) => {
    if (!isLoadingUser) {
      if (invoiceUrl) {
        window.open(`${invoiceUrl}`, '_blank')
      }
      else {
        notificationError(
          'Unable to Print Sales Order',
          'There is no sales order data.'
        )
      }
    }
  }

  const handleDeleteOrder = (dataOrder) => {
    modalDeleteData('Order', dataOrder.id, dataOrder.orderCode, deleteOrder, reloadList)
  }

  const mappingUserSales = (value) => {
    if (value !== null) {
      return (
        <Flex gap='sm' align='center' mb={16}>
          <Avatar variant='filled' name={value} radius="xl" size="md" color='red' />
          <Box>
            <Text fz={13} fw='bold' lineClamp={2}>{value}</Text>
          </Box>
        </Flex>
      )
    } else {
      return <Badge variant="light" color="black" tt='capitalize' fw='500' mb={30}>No Sales Team</Badge>
    }
  }

  const mappingUserAgent = (value) => {
    if (value.id !== null) {
      return (
        <Flex gap='sm' align='center' mb={16}>
          <Avatar variant='filled' name={value.name} radius="xl" size="md" color='red' />
          <Box>
            <Text fz={13} fw='bold' lineClamp={2}>{value.name}</Text>
          </Box>
        </Flex>
      )
    } else {
      return <Badge variant="light" color="black" tt='capitalize' fw='500' mb={30}>No Agent Team</Badge>
    }
  }

  const mappingTrackingCode = (dataPackageShipment) => {
    if (dataPackageShipment.length > 0) {
      return (<Anchor href="https://mantine.dev/" fz={12} target="_blank" underline="hover" c='blue' mt={4}>See Available Tracking</Anchor>)
    } else {
      return (<Text fz={12} c='gray.5' mt={8}>Not available yet</Text>)
    }
  }

  const stateStoreInWarehouse = (historyStatus) => {
    if (historyStatus.status_id === 46 || historyStatus.status_id === 47) {
      return (
        <Text fz={11} c='gray.6'>{dayjs(dayjs(new Date()).format('YYYY-MM-DD')).diff(`${dayjs(historyStatus.date).format('YYYY-MM-DD')}`, 'day', true)} days in warehouse</Text>
      )
    }
  }

  const mappingLayoutViewOrder = (type, val) => {
    if (type === 'all-order') {
      return (
        <SimpleGrid cols={3} verticalSpacing="md" >
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Shipment Method</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500'>{val.shipmentMethodName} {(val.shipmentMethodName && val.loadType) && val.loadType}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Terms</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500'>{val.terms} {(val.terms && val.termsNote) && val.termsNote}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Order Status</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.orderStatus}</Badge>
            {val.lastHistoryStatus ? stateStoreInWarehouse(val.lastHistoryStatus) : ''}
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Route (From - To)</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.countryProducts} - {val.destinationProducts}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Tracking Code</Text>
            {loadingPackageShipment ? <Box w={100} mt={8}><Skeleton height={20} radius="xl" /></Box> : mappingTrackingCode(packageShipment)}
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Payment Status</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.paymentStatus}</Badge>
            {val.paymentStatusDate ? <Text fz={11} c='gray.6'>{dayjs(val.paymentStatusDate).format('DD/MMMM/YYYY') } - {dayjs(val.paymentStatusDate).format('HH:mm:ss')}</Text> : ''}
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Order Category / Order Type</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.orderCategory ? val.orderCategory : 'No Category'} / {val.orderType}</Badge>
          </Box>
        </SimpleGrid>
      )
    } else if (type === 'division') {
      return (
        <SimpleGrid cols={3} verticalSpacing="md" >
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Order Category / Order Type</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.orderCategory ? val.orderCategory : 'No Category'} / {val.orderType}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Shipment Method</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500'>{val.shipmentMethodName} {(val.shipmentMethodName && val.loadType) && val.loadType}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Order Status</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.orderStatus}</Badge>
            {val.lastHistoryStatus ? stateStoreInWarehouse(val.lastHistoryStatus) : ''}
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Route (From - To)</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.countryProducts} - {val.destinationProducts}</Badge>
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Tracking Code</Text>
            {loadingPackageShipment ? <Box w={100} mt={8}><Skeleton height={20} radius="xl" /></Box> : mappingTrackingCode(packageShipment)}
          </Box>
          <Box>
            <Text fz={12} c='gray.6' mb={4}>Payment Status</Text>
            <Badge variant="light" color="black" tt='capitalize' fw='500' mb={4}>{val.paymentStatus}</Badge>
            {val.paymentStatusDate ? <Text fz={11} c='gray.6'>{dayjs(val.paymentStatusDate).format('DD/MMMM/YYYY') } - {dayjs(val.paymentStatusDate).format('HH:mm:ss')}</Text> : ''}
          </Box>
        </SimpleGrid>
      )
    }
  }

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder mb={20}>
      <Card.Section style={{ backgroundColor: '#eeefef' }} >
        <Group justify='space-between' align='center' p={12}>
          <Text fz={12} fw='bold' color='red'>{data.orderCode} { data.markingCode && ` / ${data.markingCode}` }</Text>
          <Text fz={12} fw='bold' color='red'>{ dayjs(data.orderDate).format('DD/MMMM/YYYY') }</Text>
        </Group>
      </Card.Section>
      <Box py={10}>
        <Grid>
          <Grid.Col span={2} style={{ borderRight: '1px solid #eeefef' }}>
            <Box mb={14}>
              <Text fz={12} c='gray.6' mb={4}>Order By</Text>
              {loadingUser ? loadingDataUser() : mappingDataUser(detailUser)}
            </Box>
            <Box>
              <Text fz={12} c='gray.6' mb={4}>Sales By</Text>
              {mappingUserSales(data.salesName)}
            </Box>
          </Grid.Col>
          <Grid.Col span={2} ml={12} style={{ borderRight: '1px solid #eeefef' }}>
            <Box mb={14}>
              <Text fz={12} c='gray.6' mb={4}>Customer Name</Text>
              <Flex gap='sm' align='center' mb={16}>
                <Avatar variant='filled' name={data.customerName} radius="xl" size="md" color='red' />
                <Box>
                  <Text fz={13} fw='bold' lineClamp={2}>{data.customerName}</Text>
                </Box>
              </Flex>
            </Box>
            <Box>
              <Text fz={12} c='gray.6' mb={4}>Agent By</Text>
              {loadingAgent ? loadingDataUser() : mappingUserAgent(detailAgent)}
            </Box>
          </Grid.Col>
          <Grid.Col span={{ base: 4, md: 4, lg: 5.5, xl: 6 }} ml={12} style={{ borderRight: '1px solid #eeefef' }}>
            {mappingLayoutViewOrder(type, data)}
          </Grid.Col>
          <Grid.Col span={{ base: 'content', md: 2, lg: 2, xl: 'content' }} ml={12}>
            <Box mb={12}>
              <Text fz={12} c='gray.9' mb={4}>{data.orderType === 'trading' ? 'Grand Total' : 'Total Logistic All In'}</Text>
              <Title order={6} fw={600} c='red'><NumberFormatter prefix={`${data.orderCurrency} `} thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale={true} value={data.orderType === 'trading' ? data.grandTotal : data.totalLogisticAllIn} /></Title>
            </Box>
            <Box>
              <Text fz={12} c='gray.9' mb={4}>Total Paid By User</Text>
              <Title order={4} fw={600} c='green'><NumberFormatter prefix={`${data.orderCurrency} `} thousandSeparator="," decimalSeparator="." decimalScale={2} fixedDecimalScale={true} value={data.totalPaidByUser} /></Title>
            </Box>
          </Grid.Col>
        </Grid>
      </Box>
      <Card.Section withBorder inheritPadding py="xs">
        <Flex justify='space-between' align='center'>
          <Box>
            <Flex gap="md" align='center'>
              <Text fz={10} c='gray.6'>Last Update: {dayjs(data.updatedAt).format('DD/MMMM/YYYY')} - { dayjs(data.updatedAt).format('HH:mm:ss')}</Text>
              <Text fz={10} c='gray.6'>Created Date {dayjs(data.createdAt).format('DD/MMMM/YYYY')} - { dayjs(data.createdAt).format('HH:mm:ss')}</Text>
            </Flex>
          </Box>
          <Flex gap='md' align='center'>
            <ActionIcon variant="default" size="lg" onClick={() => handlePrintOrder(data.invoiceUrl)}>
              <IconPrinter size={16} stroke={1.5} />
            </ActionIcon>
            <ActionIcon variant="default" size="lg" radius="md" onClick={() => handleDeleteOrder(data)}>
              <IconTrash size={16} stroke={1.5} />
            </ActionIcon>
            <Button size="sm" radius="md" rightSection={<IconArrowNarrowRight size={14} />} onClick={() => navigate(`/order/${data.id}`) }>Detail</Button>
          </Flex>
        </Flex>
      </Card.Section>
    </Card>
  )
}

export default OrderList